import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../contexts/SessionContext';
import API_URL from '../constants/environment';
import { OrderDetailResponse } from './useGetOrderDetail';

interface DeleteAssemblyMappingInput {
    orderId: string;
    parentPartId: string;
}

const deleteAssemblyMapping = async (data: DeleteAssemblyMappingInput, sessionId: string | null) => {
    return axios.delete(`${API_URL}/order/parts/assembly-mapping`, {
        headers: {
            Authorization: sessionId
        },
        data: { parentPartId: data.parentPartId }
    });
};

export const useDeleteAssemblyMapping = () => {
    const sessionContext = useContext(SessionContext);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: DeleteAssemblyMappingInput) => deleteAssemblyMapping(data, sessionContext?.sessionId ?? null),
        onMutate: async ({ orderId, parentPartId }: DeleteAssemblyMappingInput) => {
            await queryClient.cancelQueries({ queryKey: ['/order/detail', orderId] });

            const previousOrder = queryClient.getQueryData<OrderDetailResponse>(['/order/detail', orderId]);

            queryClient.setQueryData(['/order/detail', orderId], (old: OrderDetailResponse | undefined): OrderDetailResponse | undefined => {
                if (!old?.newParts) return old;

                const parent = old.newParts.parts.find((p) => p._id === parentPartId);
                const affectedIds = new Set([parentPartId, ...(parent?.assemblySubParts ?? [])]);

                return {
                    ...old,
                    newParts: {
                        ...old.newParts,
                        parts: old.newParts.parts.map((p) => {
                            if (p._id === parentPartId) {
                                return { ...p, assemblyColor: undefined, assemblySubParts: undefined };
                            }
                            if (affectedIds.has(p._id)) {
                                return { ...p, assemblyColor: undefined };
                            }
                            return p;
                        }),
                    },
                };
            });

            return { previousOrder };
        },
        onError: (_error, { orderId }, context) => {
            if (context?.previousOrder) {
                queryClient.setQueryData(['/order/detail', orderId], context.previousOrder);
            }
        },
        onSuccess: (_data, { orderId }) => {
            queryClient.invalidateQueries({ queryKey: ['/order/detail', orderId] });
        },
    });
};
